import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const COLUMNS = [
  ["applied", "Applied"],
  ["screened", "AI Screened"],
  ["round1_scheduled", "R1 Scheduled"],
  ["round1_feedback", "R1 Feedback"],
  ["round2_scheduled", "R2 Scheduled"],
  ["round2_feedback", "R2 Feedback"],
  ["final_scheduled", "Final Scheduled"],
  ["final_feedback", "Panel Feedback"],
  ["selected", "Selected"],
  ["round1_rejected", "Rejected R1"],
  ["round2_rejected", "Rejected R2"],
  ["final_rejected", "Rejected Final"],
];

export default function CandidatePipeline() {
  const { user } = useAuth();
  const isHR = user?.role === "hr";
  const [params] = useSearchParams();
  const requisitionId = params.get("requisition_id") || "";
  const [cands, setCands] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [reqFilter, setReqFilter] = useState(requisitionId);
  const [view, setView] = useState("kanban");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [dragOverCol, setDragOverCol] = useState(null);

  const reload = () => {
    const q = {};
    if (reqFilter) q.requisition_id = reqFilter;
    api.get("/candidates", { params: q }).then((r) => setCands(r.data));
  };

  useEffect(() => {
    api.get("/requisitions").then((r) => setReqs(r.data));
  }, []);

  useEffect(() => {
    reload();
    setSelected(new Set());
    // eslint-disable-next-line
  }, [reqFilter]);

  const grouped = COLUMNS.reduce((acc, [k]) => {
    acc[k] = cands.filter((c) => c.status === k);
    return acc;
  }, {});

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const moveCandidate = async (cid, newStatus) => {
    const cand = cands.find((c) => c.id === cid);
    if (!cand || cand.status === newStatus) return;
    // optimistic update
    setCands((prev) => prev.map((c) => (c.id === cid ? { ...c, status: newStatus } : c)));
    try {
      await api.patch(`/candidates/${cid}/status`, { status: newStatus });
      toast.success(`Moved ${cand.full_name} → ${newStatus.replace(/_/g, " ")}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
      reload();
    }
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return toast.error("Select at least one candidate");
    setBusy(true);
    try {
      const r = await api.post("/candidates/bulk-action", {
        candidate_ids: Array.from(selected),
        action,
        round: "round2",
      });
      toast.success(`${action} · ${r.data.succeeded}/${r.data.total} done`);
      setSelected(new Set());
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk action failed");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("hr_token");
    const url = new URL(`${API_BASE}/export/candidates.csv`);
    if (reqFilter) url.searchParams.set("requisition_id", reqFilter);
    fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `candidates_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("CSV downloaded");
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <div className="p-8" data-testid="candidates-page">
      <div className="flex items-end justify-between border-b border-rule pb-6 gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Workflow · Candidates</div>
          <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Pipeline.</h1>
          {isHR && (
            <p className="text-xs text-quiet mt-3 font-mono">
              Tip: drag a card between columns to override stage · select candidates for bulk actions.
            </p>
          )}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            data-testid="req-filter"
            value={reqFilter}
            onChange={(e) => setReqFilter(e.target.value)}
            className="border border-rule bg-white px-3 py-2 text-sm focus:outline-none focus:border-klein"
          >
            <option value="">All requisitions</option>
            {reqs.map((r) => (
              <option key={r.id} value={r.id}>
                {`${r.title} · ${r.requisition_code || "—"}`}
              </option>
            ))}
          </select>
          {isHR && (
            <button
              data-testid="export-csv-btn"
              onClick={exportCsv}
              className="border border-ink hover:bg-paper px-3 py-2 text-xs font-mono uppercase tracking-wider"
            >
              Export CSV
            </button>
          )}
          <div className="flex border border-rule">
            <button
              data-testid="view-kanban"
              onClick={() => setView("kanban")}
              className={`px-3 py-2 font-mono text-xs uppercase ${view === "kanban" ? "bg-ink text-white" : "text-quiet"}`}
            >
              Kanban
            </button>
            <button
              data-testid="view-list"
              onClick={() => setView("list")}
              className={`px-3 py-2 font-mono text-xs uppercase ${view === "list" ? "bg-ink text-white" : "text-quiet"}`}
            >
              List
            </button>
          </div>
        </div>
      </div>

      {isHR && selected.size > 0 && (
        <div
          className="mt-4 bg-ink text-white px-5 py-3 flex items-center justify-between fade-in"
          data-testid="bulk-action-bar"
        >
          <div className="font-mono text-xs uppercase tracking-wider">
            {selected.size} selected
          </div>
          <div className="flex gap-2">
            <button
              data-testid="bulk-screen-btn"
              disabled={busy}
              onClick={() => bulkAction("screen")}
              className="bg-klein hover:bg-klein-hover px-3 py-1.5 text-xs font-medium"
            >
              {busy ? "Working…" : "Run AI screening"}
            </button>
            <button
              data-testid="bulk-shortlist-btn"
              disabled={busy}
              onClick={() => bulkAction("shortlist")}
              className="bg-white text-ink hover:bg-paper px-3 py-1.5 text-xs font-medium"
            >
              Shortlist
            </button>
            <button
              data-testid="bulk-reject-btn"
              disabled={busy}
              onClick={() => bulkAction("reject")}
              className="border border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] px-3 py-1.5 text-xs font-medium bg-white"
            >
              Reject (R2)
            </button>
            <button
              data-testid="bulk-clear"
              onClick={() => setSelected(new Set())}
              className="text-xs text-white/70 hover:text-white px-3 py-1.5"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {view === "kanban" ? (
        <div className="mt-6 flex overflow-x-auto border border-rule bg-white" data-testid="kanban-board">
          {COLUMNS.map(([k, l]) => (
            <div
              key={k}
              data-testid={`kanban-col-${k}`}
              className={`min-w-[280px] w-[280px] border-r border-rule last:border-r-0 flex flex-col transition-colors ${
                dragOverCol === k ? "bg-paper" : ""
              }`}
              onDragOver={(e) => {
                if (!isHR) return;
                e.preventDefault();
                if (dragOverCol !== k) setDragOverCol(k);
              }}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={(e) => {
                if (!isHR) return;
                e.preventDefault();
                const cid = e.dataTransfer.getData("text/plain");
                setDragOverCol(null);
                if (cid) moveCandidate(cid, k);
              }}
            >
              <div className="px-4 py-3 border-b border-rule flex items-center justify-between bg-paper">
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink">{l}</div>
                <div className="font-mono text-xs text-quiet">{grouped[k].length}</div>
              </div>
              <div className="flex-1 p-2 space-y-2 max-h-[calc(100vh-260px)] overflow-y-auto">
                {grouped[k].map((c) => (
                  <div
                    key={c.id}
                    data-testid={`kanban-card-${c.id}`}
                    draggable={isHR}
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", c.id);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    className={`bg-white border border-rule p-3 hover:border-ink hover:-translate-y-[1px] transition-all ${
                      isHR ? "cursor-grab active:cursor-grabbing" : ""
                    } ${selected.has(c.id) ? "ring-1 ring-klein border-klein" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      {isHR && (
                        <input
                          type="checkbox"
                          data-testid={`select-${c.id}`}
                          checked={selected.has(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-0.5 accent-[#002FA7]"
                        />
                      )}
                      <Link to={`/candidates/${c.id}`} className="block flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm text-ink font-medium truncate">{c.full_name}</div>
                            <div className="text-[11px] text-quiet font-mono mt-0.5 truncate">
                              {c.requisition_title}
                            </div>
                          </div>
                          {c.ai_score?.overall != null && (
                            <div className="font-mono text-sm text-klein">{c.ai_score.overall}</div>
                          )}
                        </div>
                        <div className="mt-2 flex items-center gap-1 text-[11px] text-quiet font-mono">
                          <span>{c.relevant_experience}y</span>
                          <span>·</span>
                          <span className="truncate">{c.current_location}</span>
                        </div>
                      </Link>
                    </div>
                  </div>
                ))}
                {grouped[k].length === 0 && (
                  <div className="text-center text-quiet text-xs py-8 font-mono">— empty —</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-6 bg-white border border-rule">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rule">
                {isHR && (
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      data-testid="select-all"
                      checked={cands.length > 0 && selected.size === cands.length}
                      onChange={(e) => {
                        if (e.target.checked) setSelected(new Set(cands.map((c) => c.id)));
                        else setSelected(new Set());
                      }}
                      className="accent-[#002FA7]"
                    />
                  </th>
                )}
                <Th>Candidate</Th>
                <Th>Role</Th>
                <Th>Exp</Th>
                <Th>Score</Th>
                <Th>Status</Th>
                <Th>Applied</Th>
              </tr>
            </thead>
            <tbody>
              {cands.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-rule hover:bg-paper"
                  data-testid={`candidate-row-${c.id}`}
                >
                  {isHR && (
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        data-testid={`select-row-${c.id}`}
                        checked={selected.has(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        className="accent-[#002FA7]"
                      />
                    </td>
                  )}
                  <Td>
                    <Link to={`/candidates/${c.id}`} className="block">
                      <div className="text-ink hover:underline">{c.full_name}</div>
                      <div className="text-xs text-quiet font-mono">{c.email}</div>
                    </Link>
                  </Td>
                  <Td>{c.requisition_title}</Td>
                  <Td className="font-mono">{c.relevant_experience}y</Td>
                  <Td className="font-mono text-klein">{c.ai_score?.overall ?? "—"}</Td>
                  <Td>
                    <StatusBadge status={c.status} />
                  </Td>
                  <Td className="font-mono text-xs text-quiet">{new Date(c.created_at).toLocaleDateString()}</Td>
                </tr>
              ))}
              {cands.length === 0 && (
                <tr>
                  <td colSpan={isHR ? 7 : 6} className="text-center py-12 text-quiet">
                    No candidates yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => (
  <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-quiet">{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
